/**
 * A dependency free, page based carousel.
 *
 * The markup is a plain list of cards; this progressively enhances it by
 * adding the previous/next arrows and the numbered pagination, then paging the
 * track with a CSS transform. Without JavaScript the list simply shows the
 * first page of cards.
 *
 * The paging arithmetic is kept in the pure functions at the top of this
 * module so it can be unit tested without a DOM; everything below them is the
 * thin layer that applies those results to the page.
 *
 * @module carousel
 */
import * as icons from "@qhealth-design-system/core/src/utils/icons";

const SELECTOR = '[data-action="carousel"]';
const RESIZE_DEBOUNCE = 150;
const ENHANCED_CLASS = "qld__carousel__track--enhanced";
const DRAGGING_CLASS = "qld__carousel__track--dragging";
const STACKED_CLASS = "qld__carousel--stacked";
// Set while the arrows stand beside the cards, so the footer lays itself out
// for the arrows it actually holds rather than for a breakpoint.
const SIDE_ARROWS_CLASS = "qld__carousel--side-arrows";
const READY_FLAG = "carouselReady";
const FOCUSABLE = "a[href], button, input, [tabindex]";

// Announced in place of "region" once the cards are actually being paged, so
// the name a screen reader gives the section matches the controls it has.
const ROLE_DESCRIPTION = "aria-roledescription";

// A drag shorter than the threshold — this share of the visible frame's width
// — snaps back rather than paging, so the commitment scales with how big
// the carousel is on screen. The slop is how far the pointer may wander
// before a press counts as a drag at all, keeping ordinary clicks and taps on
// the cards intact.
const DRAG_THRESHOLD_SHARE = 0.25;
const DRAG_SLOP = 8;

// How much a drag past either end of the track is slowed, as a resistance cue
// that there is no further page in that direction.
const OVERDRAG_RESISTANCE = 3;

// A release still moving at least this fast (pixels per millisecond) is a
// flick, and turns the page however short the drag: a quick flick of the
// thumb travels only a little, but means the same as a long pull. The window
// is how far back (in milliseconds) the release looks to measure its speed.
const FLICK_VELOCITY = 0.5;
const FLICK_WINDOW = 100;

// The arrows only have room beside the cards from the xxl breakpoint up; below
// that they sit in the footer next to the pagination (see css/component.scss).
const SIDE_ARROWS_MIN_WIDTH = 1599;

// The side arrows hang in the page margin outside the carousel, which is only
// empty when the carousel spans the full container (88rem less its 3rem
// padding either side, 1312px at xxl). In a narrower column, such as beside
// the internal navigation, that margin belongs to the next column and the
// previous arrow would sit over it. A full width carousel at xxl is never
// narrower than this; one sharing the row with a sidebar is well short of it.
const SIDE_ARROWS_MIN_CAROUSEL_WIDTH = 1200;

// Marks a gap in a truncated pagination list.
export const ELLIPSIS = "ellipsis";

// A carousel needs at least this many cards, and enough of them to fill this
// many pages; anything less is stacked into rows instead. Together these give
// the per breakpoint minimums: 4 cards on xs/sm (1 per page), 5 on md (2 per
// page) and 7 on lg and up (3 per page).
const MIN_CARDS = 4;
const MIN_PAGES = 3;

// The pagination never shows more than seven entries. Seven is what it takes to
// keep the first and last page on screen either side of the current page and
// its two neighbours, with a gap marker between each run — fewer, and a
// truncated list has to drop one end, leaving the reader no anchor for where
// they are in the set. Up to seven pages they all fit, so nothing is hidden.
// The numbers are only on screen from xxl, where three cards a page means
// truncating from 22 cards up.
const MAX_ENTRIES = 7;

// Widest first: the number of cards shown is the first entry the viewport is
// wide enough for. Keep in sync with the breakpoints in css/component.scss.
const BREAKPOINTS = [
  { minWidth: 992, cards: 3 },
  { minWidth: 699, cards: 2 },
  { minWidth: 0, cards: 1 },
];

/**
 * Number of cards shown at a given viewport width.
 *
 * @memberof module:carousel
 *
 * @param {number} width - Viewport width in pixels
 *
 * @returns {number} The number of visible cards
 */
export function cardsForWidth(width) {
  return BREAKPOINTS.find((entry) => width >= entry.minWidth).cards;
}

/**
 * Number of pages needed to show every card.
 *
 * @memberof module:carousel
 *
 * @param {number} total - Total number of cards
 * @param {number} perPage - Cards shown per page
 *
 * @returns {number} The page count
 */
export function pageCount(total, perPage) {
  return Math.max(1, Math.ceil(total / perPage));
}

/**
 * Hold a page index within the available pages.
 *
 * @memberof module:carousel
 *
 * @param {number} page - Zero based page index
 * @param {number} pages - Total number of pages
 *
 * @returns {number} A page index that exists
 */
export function clampPage(page, pages) {
  return Math.min(Math.max(page, 0), pages - 1);
}

/**
 * Index of the first card shown on a page.
 *
 * A final page with too few cards left to fill it is pulled back over
 * the previous one, so every page is full. With seven cards three at a time
 * the last page shows the fifth, sixth and seventh rather than the seventh
 * alone.
 *
 * @memberof module:carousel
 *
 * @param {number} page - Zero based page index
 * @param {number} perPage - Cards shown per page
 * @param {number} total - Total number of cards
 *
 * @returns {number} Zero based index of the first visible card
 */
export function pageStart(page, perPage, total) {
  return Math.min(page * perPage, Math.max(0, total - perPage));
}

/**
 * The cards visible on a page, as one based positions in the full list.
 *
 * @memberof module:carousel
 *
 * @param {number} page - Zero based page index
 * @param {number} perPage - Cards shown per page
 * @param {number} total - Total number of cards
 *
 * @returns {{first: number, last: number}} The visible range
 */
export function visibleRange(page, perPage, total) {
  const first = pageStart(page, perPage, total) + 1;

  return { first, last: Math.min(first + perPage - 1, total) };
}

/**
 * Whether the arrows sit either side of the cards, rather than in the footer
 * beside the pagination. Both widths have to allow it: the viewport for there
 * to be a page margin at all, and the carousel for that margin to be beside it
 * rather than another column's.
 *
 * @memberof module:carousel
 *
 * @param {number} width - Viewport width in pixels
 * @param {number} carouselWidth - Width of the carousel section in pixels
 *
 * @returns {boolean} True when the arrows belong beside the cards
 */
export function usesSideArrows(width, carouselWidth) {
  return (
    width >= SIDE_ARROWS_MIN_WIDTH &&
    carouselWidth >= SIDE_ARROWS_MIN_CAROUSEL_WIDTH
  );
}

/**
 * Whether a set of cards is paged as a carousel, or stacked into rows.
 *
 * A carousel needs four or more cards and enough of them to fill three pages,
 * so the pagination is never shorter than three. Content that would fit in two
 * pages is stacked into two rows instead.
 *
 * @memberof module:carousel
 *
 * @param {number} cards - Total number of cards
 * @param {number} perPage - Cards shown per page at this viewport width
 *
 * @returns {boolean} True when the cards should be paged
 */
export function usesCarousel(cards, perPage) {
  return cards >= MIN_CARDS && pageCount(cards, perPage) >= MIN_PAGES;
}

// The page numbers from one to the other, both ends included.
const range = (from, to) =>
  Array.from({ length: to - from + 1 }, (_, index) => from + index);

/**
 * The pagination entries for a page.
 *
 * Up to seven pages every page number is listed, since they all fit. Beyond
 * that the first and last page are always shown, so the reader can see both
 * ends of the set and jump to either, and the run between them follows the
 * current page: the opening five while it is near the start, the closing five
 * while it is near the end, and the current page with a neighbour either side
 * in between. The list is always seven entries wide, so it holds its width as
 * the pages move:
 *
 *   page 1 of 6   ->  1, 2, 3, 4, 5, 6
 *   page 1 of 9   ->  1, 2, 3, 4, 5, ellipsis, 9
 *   page 4 of 9   ->  1, 2, 3, 4, 5, ellipsis, 9
 *   page 5 of 9   ->  1, ellipsis, 4, 5, 6, ellipsis, 9
 *   page 6 of 9   ->  1, ellipsis, 5, 6, 7, 8, 9
 *   page 9 of 9   ->  1, ellipsis, 5, 6, 7, 8, 9
 *
 * @memberof module:carousel
 *
 * @param {number} current - One based current page
 * @param {number} total - Total number of pages
 *
 * @returns {Array<number|string>} Page numbers interleaved with ELLIPSIS
 */
export function paginationItems(current, total) {
  if (total <= MAX_ENTRIES) {
    return range(1, total);
  }

  // A run from one end, plus the far end and the gap marker between them, is
  // the seven; the run covers the current page while it is inside that end.
  // Past both, each end is a lone jump point and the run that fits between
  // them is the current page and a neighbour either side.
  const run = MAX_ENTRIES - 2;
  let numbers;

  if (current < run) {
    numbers = [...range(1, run), total];
  } else if (current > total - run + 1) {
    numbers = [1, ...range(total - run + 1, total)];
  } else {
    numbers = [1, current - 1, current, current + 1, total];
  }

  // The ellipsis only earns its place where it stands for a hidden page.
  return numbers.flatMap((entry, index) =>
    index > 0 && entry - numbers[index - 1] > 1 ? [ELLIPSIS, entry] : [entry],
  );
}

/**
 * The message announced in the live region after paging.
 *
 * @memberof module:carousel
 *
 * @param {number} page - Zero based page index
 * @param {number} perPage - Cards shown per page
 * @param {number} total - Total number of cards
 *
 * @returns {string} The announcement
 */
export function statusMessage(page, perPage, total) {
  const { first, last } = visibleRange(page, perPage, total);

  return `Showing items ${first} to ${last} of ${total}`;
}

/**
 * The drag distance that pages, for a given track width.
 *
 * A quarter of the visible frame: far enough that a page turn is a deliberate
 * choice at any screen size, near enough that a flick clears it comfortably.
 *
 * @memberof module:carousel
 *
 * @param {number} width - Width of the track (one frame of cards) in pixels
 *
 * @returns {number} The threshold in pixels
 */
export function dragThreshold(width) {
  return width * DRAG_THRESHOLD_SHARE;
}

/**
 * Pages moved by a released drag, from its signed distance and speed.
 *
 * A drag follows the pointer while it is down; on release anything past the
 * threshold turns one page — leftwards (negative) to the next, rightwards to
 * the previous — and anything shorter snaps back to the page it left. A
 * release still moving at flick speed in the direction of the drag also turns
 * the page, so a quick flick works however short its travel. One page per
 * gesture, however hard the flick: paging further would leave the pagination
 * and announcements skipping steps.
 *
 * @memberof module:carousel
 *
 * @param {number} distance - Signed horizontal distance in pixels
 * @param {number} threshold - Minimum distance that pages, in pixels
 * @param {number} [velocity] - Signed speed at release, in pixels per millisecond
 *
 * @returns {number} -1, 0 or 1 pages
 */
export function dragPages(distance, threshold, velocity = 0) {
  const flicked =
    Math.abs(velocity) >= FLICK_VELOCITY &&
    Math.sign(velocity) === Math.sign(distance);

  if (Math.abs(distance) < threshold && !flicked) {
    return 0;
  }

  return distance < 0 ? 1 : -1;
}

/**
 * Enhance a carousel track.
 *
 * @memberof module:carousel
 *
 * @param {HTMLElement} track - The list of carousel items
 *
 * @returns {{goTo: Function, currentPage: Function, destroy: Function}} The carousel controls
 */
export function createCarousel(track) {
  const section = track.closest(".qld__carousel");
  const viewport = track.closest(".qld__carousel__viewport");
  const pagination = section.querySelector(".qld__carousel__pagination");
  const controls = section.querySelector(".qld__carousel__controls");
  const status = section.querySelector(".qld__carousel__status");
  const items = Array.from(track.children);
  const teardown = [];
  // What this carousel is called, from the aria-label the template puts on the
  // section (see html/carousel.hbs). The controls borrow it so that a page
  // full of carousels does not present several identical "Next" buttons.
  const name = section.getAttribute("aria-label") || "carousel";

  let page = 0;
  let perPage = cardsForWidth(window.innerWidth);
  let prev;
  let next;

  const pages = () => pageCount(items.length, perPage);
  const stacked = () => !usesCarousel(items.length, perPage);
  // Offsets are a share of the track width, and each card is 100 / perPage of
  // it, so a full page is 100% and a backfilled last page is less.
  const pageOffset = () =>
    (visibleRange(page, perPage, items.length).first - 1) * (100 / perPage);

  /**
   * Add a listener and register its removal with destroy().
   *
   * @param {EventTarget} target - What to listen on
   * @param {string} type - The event name
   * @param {Function} handler - The listener
   * @param {object} [options] - Listener options
   */
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    teardown.push(() => target.removeEventListener(type, handler, options));
  };

  /**
   * Add or remove the focusable elements of a card from the tab order.
   *
   * @param {HTMLElement} item - A carousel item
   * @param {boolean} focusable - Whether the card can be tabbed to
   */
  const setFocusable = (item, focusable) => {
    item.querySelectorAll(FOCUSABLE).forEach((target) => {
      if (focusable) {
        target.removeAttribute("tabindex");
      } else {
        target.setAttribute("tabindex", "-1");
      }
    });
  };

  /**
   * Show every card as rows, with the paging controls removed. The arrows are
   * taken out of the DOM rather than hidden, because at xxl they live beside
   * the cards where the stacked styles do not reach them.
   */
  const renderStacked = () => {
    page = 0;
    section.classList.add(STACKED_CLASS);
    section.classList.remove(SIDE_ARROWS_CLASS);
    track.classList.remove(ENHANCED_CLASS);
    track.style.transform = "";
    // Rows of cards, with nothing to page: calling the region a carousel would
    // promise controls that are not there.
    section.removeAttribute(ROLE_DESCRIPTION);

    prev.remove();
    next.remove();

    if (pagination) {
      pagination.innerHTML = "";
    }

    items.forEach((item) => {
      item.removeAttribute("aria-hidden");
      setFocusable(item, true);
    });
  };

  /**
   * Reflect the current page in the track position and the controls.
   */
  const render = () => {
    if (stacked()) {
      renderStacked();
      return;
    }

    const { first, last } = visibleRange(page, perPage, items.length);

    section.classList.remove(STACKED_CLASS);
    track.classList.add(ENHANCED_CLASS);
    // The cards are paged, so the region is a carousel rather than the plain
    // one the markup describes. Its name comes from the aria-label the
    // template sets (see html/carousel.hbs).
    section.setAttribute(ROLE_DESCRIPTION, "carousel");
    // Puts the arrows back if this carousel was previously stacked.
    placeArrows();

    track.style.transform = `translateX(-${pageOffset()}%)`;

    items.forEach((item, index) => {
      // Cards on other pages are off screen, so keep them out of the tab order
      // and away from screen readers.
      const visible = index + 1 >= first && index + 1 <= last;

      item.setAttribute("aria-hidden", visible ? "false" : "true");
      setFocusable(item, visible);
    });

    // aria-disabled rather than the disabled property: a disabled button
    // leaves the tab order, so paging to either end with the keyboard would
    // drop focus off the arrow and back to the body. goTo() ignores a page
    // that is not there, so the click these still fire does nothing.
    prev.setAttribute("aria-disabled", page === 0 ? "true" : "false");
    next.setAttribute("aria-disabled", page === pages() - 1 ? "true" : "false");

    renderPagination();
  };

  /**
   * Move focus to the first card of the page on show.
   */
  const focusCurrentPage = () => {
    const { first } = visibleRange(page, perPage, items.length);
    const target = items[first - 1].querySelector(FOCUSABLE);

    if (target) {
      target.focus();
    }
  };

  /**
   * Move to a page, ignoring anything out of range.
   *
   * @param {number} target - Zero based page index
   */
  const goTo = (target) => {
    if (stacked() || target < 0 || target > pages() - 1 || target === page) {
      return;
    }

    // Paging with the arrow keys leaves focus on the card it started from,
    // which render() is about to hide from screen readers and take out of the
    // tab order. Carry focus over to the page that replaced it instead, so it
    // never rests on a card that is no longer there.
    const held = track.contains(document.activeElement);

    page = target;
    render();

    if (held) {
      focusCurrentPage();
    }

    if (status) {
      status.textContent = statusMessage(page, perPage, items.length);
    }
  };

  /**
   * Create a single arrow button.
   *
   * @param {string} direction - Either "prev" or "next"
   * @param {string} label - The accessible label for the button
   *
   * @returns {HTMLElement} The arrow button
   */
  const createArrow = (direction, label) => {
    const button = document.createElement("button");
    const chevron = direction === "prev" ? "left" : "right";
    // section.dataset.path is the site's icon sprite path (Squiz asset link
    // or plain .svg path in Storybook), threaded through from the "data-path"
    // attribute in component.hbs.
    const svgPath = icons.validateInternalSvgPath(section.dataset.path);

    button.type = "button";
    button.className = `qld__carousel__arrow qld__carousel__arrow--${direction}`;

    if (svgPath) {
      // Static markup around a path validateInternalSvgPath has vetted, so
      // this is the one innerHTML in the module.
      button.innerHTML = `<svg class="qld__icon qld__icon--xs" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><use href="${icons.buildIconPath(svgPath, `chevron-${chevron}`)}"></use></svg>`;
    }

    // textContent, not innerHTML: the label carries the section's aria-label,
    // which is CMS-authored. Handlebars escapes it into the attribute, but
    // getAttribute hands it back decoded, so interpolated into innerHTML it
    // would parse as markup.
    const srLabel = document.createElement("span");

    srLabel.className = "sr-only";
    srLabel.textContent = label;
    button.appendChild(srLabel);

    listen(button, "click", () => goTo(page + (direction === "prev" ? -1 : 1)));

    return button;
  };

  /**
   * Create a single page number button.
   *
   * @param {number} number - One based page number
   * @param {number} total - Total number of pages
   *
   * @returns {HTMLElement} The list item containing the page number button
   */
  const createDot = (number, total) => {
    const item = document.createElement("li");
    const button = document.createElement("button");
    const current = number === page + 1;
    // Only numbers, but assembled the same way as the arrow labels so that no
    // text in the module goes through the HTML parser.
    const srSuffix = document.createElement("span");

    srSuffix.className = "sr-only";
    srSuffix.textContent = ` of ${total} pages`;

    button.type = "button";
    button.dataset.page = String(number - 1);
    button.append(String(number), srSuffix);
    // "page" rather than the bare "true": it is the token core's own
    // pagination marks its current number with, so the two read alike where a
    // page carries both.
    button.setAttribute("aria-current", current ? "page" : "false");

    item.className = current ? "qld__carousel__page--current" : "";
    item.appendChild(button);

    return item;
  };

  /**
   * Create the gap marker between two runs of page numbers.
   *
   * @returns {HTMLElement} The list item holding the ellipsis
   */
  const createEllipsis = () => {
    const item = document.createElement("li");

    item.className = "qld__carousel__ellipsis";
    item.setAttribute("aria-hidden", "true");
    item.textContent = "•••";

    return item;
  };

  /**
   * Build (or rebuild) the pagination for the current page: the page numbers
   * (where the arrows have moved beside the cards) and the "1 of 9" counter
   * (wherever they are in the footer). Both are rendered and the CSS shows
   * whichever suits the placement, so nothing here depends on the screen width.
   */
  const renderPagination = () => {
    if (!pagination) {
      return;
    }

    const total = pages();
    const counter = document.createElement("span");
    const list = document.createElement("ul");
    // The entries shift as the current page moves, so keep the keyboard on the
    // pagination when it is what triggered the change.
    const hadFocus = pagination.contains(document.activeElement);

    list.className = "qld__carousel__pages";

    paginationItems(page + 1, total).forEach((entry) => {
      list.appendChild(
        entry === ELLIPSIS ? createEllipsis() : createDot(entry, total),
      );
    });

    counter.className = "qld__carousel__counter";
    counter.textContent = `${page + 1} of ${total}`;

    pagination.innerHTML = "";
    pagination.appendChild(list);
    pagination.appendChild(counter);

    if (hadFocus) {
      const active = pagination.querySelector(
        ".qld__carousel__page--current button",
      );

      if (active) {
        active.focus();
      }
    }
  };

  /**
   * Put the arrows either side of the cards on the widest screens, and in the
   * footer beside the pagination on everything smaller.
   */
  const placeArrows = () => {
    if (stacked()) {
      return;
    }

    const side = usesSideArrows(
      window.innerWidth,
      section.getBoundingClientRect().width,
    );
    const target = side ? viewport : controls;

    section.classList.toggle(SIDE_ARROWS_CLASS, side);

    if (!target || prev.parentNode === target) {
      return;
    }

    target.appendChild(prev);
    target.appendChild(next);
  };

  /**
   * React to a viewport that crossed one of the carousel's breakpoints.
   */
  const bindResize = () => {
    let timer;

    listen(window, "resize", () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const cards = cardsForWidth(window.innerWidth);

        placeArrows();

        if (cards === perPage) {
          return;
        }

        perPage = cards;
        // The page count may have shrunk with the viewport, and the carousel
        // may now be short enough to stack (or long enough to page).
        page = clampPage(page, pages());
        render();
      }, RESIZE_DEBOUNCE);
    });

    teardown.push(() => window.clearTimeout(timer));
  };

  /**
   * Page with the arrow keys.
   */
  const bindKeys = () => {
    listen(track, "keydown", (event) => {
      if (event.key === "ArrowLeft") {
        goTo(page - 1);
      } else if (event.key === "ArrowRight") {
        goTo(page + 1);
      }
    });
  };

  /**
   * Follow the pointer while it drags the track, then settle on release.
   *
   * Pointer events cover a thumb on a touch screen and a held mouse button
   * alike. The track's touch-action: pan-y (see css/component.scss) leaves
   * vertical scrolling to the browser, which cancels the drag when it takes
   * the gesture over.
   */
  const bindDrag = () => {
    let startX = null;
    let dragged = false;
    let threshold = 0;
    let samples = [];

    /**
     * The pointer's speed at release, from the recent movement samples.
     *
     * Measured against the oldest sample within the flick window, so a flick
     * is judged on how the gesture ended: a drag out and a rest before
     * releasing has no recent movement and no speed.
     *
     * @param {PointerEvent} event - The releasing pointerup
     *
     * @returns {number} Signed speed in pixels per millisecond
     */
    const releaseVelocity = (event) => {
      const oldest = samples.find(
        (sample) => event.timeStamp - sample.time <= FLICK_WINDOW,
      );

      return oldest && event.timeStamp > oldest.time
        ? (event.clientX - oldest.x) / (event.timeStamp - oldest.time)
        : 0;
    };

    /**
     * Land on a page: the neighbour when the drag travelled far enough or
     * ended in a flick, or back on the page the drag started from.
     *
     * @param {number} distance - Signed horizontal distance in pixels
     * @param {number} velocity - Signed speed at release, in pixels per millisecond
     */
    const settle = (distance, velocity) => {
      const target = clampPage(
        page + dragPages(distance, threshold, velocity),
        pages(),
      );

      startX = null;
      track.classList.remove(DRAGGING_CLASS);

      if (target !== page) {
        goTo(target);
      } else {
        // Animates the snap back, since the dragging class no longer
        // suppresses the transition.
        track.style.transform = `translateX(-${pageOffset()}%)`;
      }
    };

    listen(track, "pointerdown", (event) => {
      if (stacked() || !event.isPrimary || event.button !== 0) {
        return;
      }

      startX = event.clientX;
      dragged = false;
      samples = [];
      // The track is exactly one frame wide, so this scales the threshold to
      // the carousel as it is on screen when the drag begins.
      threshold = dragThreshold(track.getBoundingClientRect().width);
    });

    listen(track, "pointermove", (event) => {
      if (startX === null) {
        return;
      }

      const distance = event.clientX - startX;

      // Inside the slop a press is still a click, not a drag.
      if (!dragged && Math.abs(distance) < DRAG_SLOP) {
        return;
      }

      if (!dragged) {
        // Keeps the pointer events coming when the pointer leaves the track
        // mid drag. Taken only once the press is a drag: a captured pointer's
        // click goes to the track rather than the card link under it, so
        // capturing on pointerdown would swallow every ordinary click. Capture
        // is an enhancement: synthetic pointer events (tests) have no active
        // pointer to capture, and old test DOMs lack the method.
        try {
          track.setPointerCapture(event.pointerId);
        } catch {
          // Carry on uncaptured.
        }
      }

      dragged = true;
      track.classList.add(DRAGGING_CLASS);

      // Movement history for the flick measurement, trimmed to its window.
      samples.push({ x: event.clientX, time: event.timeStamp });
      samples = samples.filter(
        (sample) => event.timeStamp - sample.time <= FLICK_WINDOW,
      );

      // Dragging past either end meets resistance: the track follows at a
      // fraction of the pointer, showing there is no page in that direction.
      const overdrag =
        (page === 0 && distance > 0) || (page === pages() - 1 && distance < 0);
      const followed = overdrag ? distance / OVERDRAG_RESISTANCE : distance;

      track.style.transform = `translateX(calc(-${pageOffset()}% + ${followed}px))`;
    });

    listen(track, "pointerup", (event) => {
      if (startX !== null) {
        settle(event.clientX - startX, releaseVelocity(event));
      }
    });

    // The browser claimed the gesture (vertical scroll, most likely).
    listen(track, "pointercancel", () => {
      if (startX !== null) {
        settle(0, 0);
      }
    });

    // A drag that started on a card must not end as a click on its link, and
    // the links and images must not start a native drag of their own.
    listen(
      track,
      "click",
      (event) => {
        if (dragged) {
          dragged = false;
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true,
    );
    listen(track, "dragstart", (event) => event.preventDefault());
  };

  /**
   * Remove the controls and listeners, leaving the original markup behind.
   */
  const destroy = () => {
    teardown.splice(0).forEach((remove) => remove());

    prev.remove();
    next.remove();

    if (pagination) {
      pagination.innerHTML = "";
    }

    track.classList.remove(ENHANCED_CLASS);
    track.classList.remove(DRAGGING_CLASS);
    section.classList.remove(STACKED_CLASS);
    section.classList.remove(SIDE_ARROWS_CLASS);
    section.removeAttribute(ROLE_DESCRIPTION);
    track.style.transform = "";
    delete track.dataset[READY_FLAG];

    if (controls) {
      controls.removeAttribute("role");
      controls.removeAttribute("aria-label");
    }

    items.forEach((item) => {
      item.removeAttribute("aria-hidden");
      setFocusable(item, true);
    });
  };

  // Phrased as the core pagination component labels its own arrows ("Previous
  // page of results"), so the two read alike where a page carries both.
  prev = createArrow("prev", `Previous page of ${name}`);
  next = createArrow("next", `Next page of ${name}`);
  placeArrows();

  // Names the page numbers as a set. The arrows are only in here below xxl —
  // above it they sit beside the cards — which is the other reason they carry
  // the carousel's name themselves rather than leaning on this.
  if (controls) {
    controls.setAttribute("role", "group");
    controls.setAttribute("aria-label", `${name} pagination`);
  }

  if (pagination) {
    // Delegated so the page numbers can be rebuilt on every page change without
    // accumulating listeners.
    listen(pagination, "click", (event) => {
      const button = event.target.closest("button[data-page]");

      if (button) {
        goTo(Number(button.dataset.page));
      }
    });
  }

  bindResize();
  bindKeys();
  bindDrag();
  track.dataset[READY_FLAG] = "true";
  // Decides between the paged and stacked layouts for the current width.
  render();

  return { goTo, currentPage: () => page, destroy };
}

/**
 * Initialise every carousel within a root element.
 *
 * @memberof module:carousel
 *
 * @param {ParentNode} [root] - Where to look for carousels, defaults to the document
 *
 * @returns {object[]} The initialised carousels
 */
export function initCarousels(root = document) {
  // Enhancing twice would append a second set of arrows over the first.
  const tracks = Array.from(root.querySelectorAll(SELECTOR)).filter(
    (track) => !track.dataset[READY_FLAG],
  );

  // Too few cards to ever page, at any width: mark the section stacked and
  // leave the rows to the CSS. There are no controls worth building.
  tracks
    .filter((track) => track.children.length < MIN_CARDS)
    .forEach((track) => {
      const section = track.closest(".qld__carousel");

      if (section) {
        section.classList.add(STACKED_CLASS);
      }
    });

  return tracks
    .filter((track) => track.children.length >= MIN_CARDS)
    .map(createCarousel);
}

// Guarded so the pure helpers above can be imported in a node test run.
if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initCarousels());
}
